export interface BrowserHttpErrorEntry {
    readonly url: string
    readonly method?: string
    readonly status: number
    readonly statusText?: string
    readonly resourceType?: string
    readonly capturedAt?: string
}

/**
 * Keep the HTTP error allowlist explicit and narrow. Proposal §139 is about
 * turning unexpected browser-visible HTTP failures into first-class signals, so
 * broad path-class suppression is not allowed here.
 */
export interface BrowserHttpErrorAllowlistRule {
    readonly status?: number
    readonly method?: string
    readonly pathPrefix?: string
    readonly urlIncludes?: string
    readonly loopbackOnly?: boolean
}

export const HTTP_ERROR_ALLOWLIST: readonly BrowserHttpErrorAllowlistRule[] = [
    // The browser E2E harness intentionally probes object URLs before falling
    // back to batched fetches during fresh-workspace bootstrap and cache-miss
    // recovery. Those local in-memory 404s are expected coordination signals,
    // not user-visible regressions.
    {
        status: 404,
        method: 'GET',
        pathPrefix: '/api/cryptoblobstore/blobs/',
        urlIncludes: '/objects/',
        loopbackOnly: true,
    },
    // A 409 on updateHead is the expected CAS merge signal for concurrent
    // browser writers. The client turns it into a pull/merge/retry flow.
    {
        status: 409,
        method: 'POST',
        pathPrefix: '/api/cryptoblobstore/blobs/',
        urlIncludes: '/heads',
        loopbackOnly: true,
    },
]

export const MAX_RUNTIME_BROWSER_HTTP_ERRORS = 20

const RUNTIME_HTTP_ERROR_STORAGE_KEY = 'sb:runtime-http-errors'

let runtimeHttpErrors: BrowserHttpErrorEntry[] = []

function isBrowserHttpErrorEntry(value: unknown): value is BrowserHttpErrorEntry {
    return typeof value === 'object'
        && value !== null
        && typeof (value as BrowserHttpErrorEntry).url === 'string'
        && Number.isFinite((value as BrowserHttpErrorEntry).status)
}

function readPersistedRuntimeHttpErrors(): BrowserHttpErrorEntry[] {
    if (typeof sessionStorage === 'undefined') {
        return runtimeHttpErrors
    }

    try {
        const raw = sessionStorage.getItem(RUNTIME_HTTP_ERROR_STORAGE_KEY)
        if (!raw) {
            return runtimeHttpErrors
        }

        const parsed = JSON.parse(raw)
        if (!Array.isArray(parsed)) {
            return runtimeHttpErrors
        }

        const normalized = parsed
            .filter(isBrowserHttpErrorEntry)
            .map(normalizeRuntimeEntry)
            .slice(-MAX_RUNTIME_BROWSER_HTTP_ERRORS)
        const normalizedRaw = normalized.length > 0 ? JSON.stringify(normalized) : null
        if (normalizedRaw !== raw) {
            persistRuntimeHttpErrors(normalized)
            return normalized
        }

        runtimeHttpErrors = normalized
        return normalized
    } catch {
        return runtimeHttpErrors
    }
}

function persistRuntimeHttpErrors(entries: BrowserHttpErrorEntry[]): void {
    runtimeHttpErrors = entries

    if (typeof sessionStorage === 'undefined') {
        return
    }

    try {
        if (entries.length === 0) {
            sessionStorage.removeItem(RUNTIME_HTTP_ERROR_STORAGE_KEY)
            return
        }

        sessionStorage.setItem(RUNTIME_HTTP_ERROR_STORAGE_KEY, JSON.stringify(entries))
    } catch {
        // Best-effort persistence only.
    }
}

function isLoopbackHostname(hostname: string): boolean {
    return hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '[::1]'
}

function sanitizeHttpErrorUrl(url: string): string {
    try {
        const parsed = new URL(
            url,
            typeof window !== 'undefined' ? window.location.origin : 'http://localhost',
        )
        return `${parsed.origin}${parsed.pathname}`
    } catch {
        return url.split(/[?#]/, 1)[0] ?? url
    }
}

function normalizeMethod(method?: string): string | undefined {
    const trimmed = method?.trim().toUpperCase()
    return trimmed ? trimmed : undefined
}

function normalizeOptionalString(value: unknown): string | undefined {
    if (typeof value !== 'string') {
        return undefined
    }

    const trimmed = value.trim()
    return trimmed ? trimmed : undefined
}

function normalizeRuntimeEntry(entry: BrowserHttpErrorEntry): BrowserHttpErrorEntry {
    const statusText = normalizeOptionalString(entry.statusText)
    const resourceType = normalizeOptionalString(entry.resourceType)
    const capturedAt = normalizeOptionalString(entry.capturedAt) ?? new Date().toISOString()

    return {
        url: sanitizeHttpErrorUrl(entry.url),
        method: normalizeMethod(entry.method),
        status: entry.status,
        ...(statusText ? { statusText } : {}),
        ...(resourceType ? { resourceType } : {}),
        capturedAt,
    }
}

export function matchesHttpErrorAllowlist(
    entry: BrowserHttpErrorEntry,
    rules: readonly BrowserHttpErrorAllowlistRule[],
): boolean {
    let pathname: string | undefined
    let hostname: string | undefined

    for (const rule of rules) {
        if (rule.status != null && rule.status !== entry.status) {
            continue
        }

        if (rule.method != null && rule.method.toUpperCase() !== (entry.method ?? 'GET').toUpperCase()) {
            continue
        }

        if (rule.pathPrefix != null || rule.loopbackOnly) {
            try {
                const url = new URL(entry.url)
                pathname ??= url.pathname
                hostname ??= url.hostname
            } catch {
                continue
            }
        }

        if (rule.loopbackOnly && (!hostname || !isLoopbackHostname(hostname))) {
            continue
        }

        if (rule.pathPrefix != null && !pathname?.startsWith(rule.pathPrefix)) {
            continue
        }

        if (rule.urlIncludes != null && !entry.url.includes(rule.urlIncludes)) {
            continue
        }

        return true
    }

    return false
}

export function isAllowlistedHttpError(entry: BrowserHttpErrorEntry): boolean {
    return matchesHttpErrorAllowlist(entry, HTTP_ERROR_ALLOWLIST)
}

export function recordBrowserHttpError(entry: BrowserHttpErrorEntry): BrowserHttpErrorEntry | null {
    if (!Number.isFinite(entry.status) || entry.status < 400) {
        return null
    }

    const runtimeEntry = normalizeRuntimeEntry(entry)
    if (isAllowlistedHttpError(runtimeEntry)) {
        return null
    }

    const entries = [...readPersistedRuntimeHttpErrors(), runtimeEntry].slice(-MAX_RUNTIME_BROWSER_HTTP_ERRORS)
    persistRuntimeHttpErrors(entries)
    return runtimeEntry
}

export function recordBrowserHttpErrorResponse(
    response: Pick<Response, 'status' | 'statusText'>,
    context: {
        url?: string
        method?: string
        resourceType?: string
    } = {},
): BrowserHttpErrorEntry | null {
    if (!Number.isFinite(response.status) || response.status < 400) {
        return null
    }

    return recordBrowserHttpError({
        url: context.url ?? '(unknown)',
        method: context.method,
        status: response.status,
        statusText: response.statusText || undefined,
        resourceType: context.resourceType,
    })
}

export function getRecentBrowserHttpErrors(limit = MAX_RUNTIME_BROWSER_HTTP_ERRORS): BrowserHttpErrorEntry[] {
    const entries = readPersistedRuntimeHttpErrors()
    if (!Number.isFinite(limit) || limit >= MAX_RUNTIME_BROWSER_HTTP_ERRORS) {
        return [...entries]
    }

    const normalizedLimit = Math.max(0, Math.trunc(limit))
    if (normalizedLimit === 0) {
        return []
    }

    return entries.slice(-normalizedLimit)
}

export function clearRecentBrowserHttpErrors(): void {
    persistRuntimeHttpErrors([])
}

export function formatHttpError(entry: BrowserHttpErrorEntry): string {
    const method = entry.method ?? 'GET'
    const resourceType = entry.resourceType ? ` [${entry.resourceType}]` : ''
    const statusText = entry.statusText ? ` ${entry.statusText}` : ''
    return `${method} ${entry.url} -> ${entry.status}${statusText}${resourceType}`
}

export function formatHttpErrors(entries: readonly BrowserHttpErrorEntry[]): string {
    return entries.map((entry, index) => `${index + 1}. ${formatHttpError(entry)}`).join('\n')
}
